import React, { Component } from 'react';
import axios from 'axios';
import Bowser from 'bowser';
import loadingGif from '../images/ajax-loader.gif';
import { StaticTable, TargetEnrollmentTable, CustomizedTable, FundUnderlyingRateInformation } from './Tables';
import { InfoSection } from './Info';
import { Footnotes } from './Footnotes';
import { Legal } from './Legal';
import { formatDropDownDate, formatMonthData, parsePercentage } from '../utils/DataFormatter';
import smoothScroll from '../utils/SmoothScroll';
import { ErrorBoundary } from './ErrorBoundary';

const frontendConfig = window.frontendConfig;

function configureRequest(apiPath) {
  axios.defaults.headers.common.Authorization = 'no~auth';
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  return frontendConfig.api + apiPath;
}

// assuming Date fields exist
const sortByDate = (items) => items.sort((itemA, itemB) => {
  // Turn your strings into dates, and then subtract their number of milliseconds since 1 January 1970
  // to get a value that is either negative, positive, or zero.
  return (new Date(itemB.Date).getTime()) - (new Date(itemA.Date).getTime());
});

const bowser = Bowser.getParser(window.navigator.userAgent).parsedResult;

class ReturnsApp extends Component {
  state = {
    title: 'Investment Option Performance',
    index: 0,
    selectedMonth: {
      Date: null,
      targetEnrollment: [],
      static: [],
      customized: [],
    },
    months: [],
    hasReturns: false,
    hasYields: false,
    FDICRate: null,
    FDICYield: null,
    StableValueYield: null,
    loading: true,
    showScroll: false,
    apiError: ''
  };

  updateIndex = (newIndex) => {
    this.setState({ index: newIndex, selectedMonth: this.state.months[newIndex] });
  };

  handleScrollEvent = () => {
    const { showScroll } = this.state;
    if (window.scrollY > 100) {
      if (!this.state.showScroll) {
        this.setState({
          showScroll: true
        });
      }
    }
    else if (showScroll) {
      this.setState({
        showScroll: false
      });
    }
  };

  static print() {
    if (window.ga !== undefined) {
      try {
        window.ga('send', 'event', {
          eventCategory: 'PerformanceReturnsPage',
          eventAction: 'Print',
          hitCallback: () => {
            window.print();
          },
          hitCallbackFail: () => {
            console.log('Unable to send Google Analytics data'); // eslint-disable-line
            window.print();
          }
        });
      }
      catch (e) {
        window.print();
      }
    }
    else {
      window.print();
    }
  }

  static handleScrollButtonClick() {
    smoothScroll(0, 0, 1000);
  }

  warningMessageCompose() {
    return (
      <div className='browserWarningContainer'>
        <span>This page is optimized for a desktop browser. Mobile browsing may not offer the best user experience.</span>
        <div
          className='browserWarningClose'
          onClick={() => {
            sessionStorage.setItem('hasMobileWarningDisplayed', true);
            this.forceUpdate();
          }}
        >
          <button className='browserWarningBtn'>dismiss</button>
        </div>
      </div>
    );
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScrollEvent);
    const getReturnsEndPoint = configureRequest('/returns/getdata');
    const getYieldsEndPoint = configureRequest('/yields');
    Promise.all([
      axios.get(getReturnsEndPoint),
      axios.get(getYieldsEndPoint)
    ])
      .then(([{ data: returns }, { data: yields }]) => {
        // assuming yields data are date independent and it should be just one item in the list
        if (yields && yields.length > 0) {
          this.setState({
            hasYields: true,
            FDICRate: parsePercentage(yields[0].FDICRate, 3),
            FDICYield: parsePercentage(yields[0].FDICYield, 3),
            StableValueYield: parsePercentage(yields[0].StableValueYield, 3),
          });
        }
        else {
          this.setState({ hasYields: false });
        }

        if (returns && returns.length > 0) {
          // first sort both by date so their order matches
          returns = sortByDate(returns);
          const months = returns.map(item => formatMonthData(new Date(item.Date), item.Returns));
          this.setState({
            hasReturns: true,
            loading: false,
            selectedMonth: months[0], // sorted by date from the latest
            months,
          });
        }
        else {
          this.setState({ hasReturns: false, loading: false });
        }
      })
      .catch(err => {
        this.setState({ hasReturns: false, hasYields: false, loading: false, apiError: err.toString() });
      });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScrollEvent);
  }

  render() {
    const {
      title, hasReturns, hasYields, loading, index, months, selectedMonth,
      showScroll, apiError, FDICRate, FDICYield, StableValueYield
    } = this.state;

    let hasMobileWarningDisplayed = true;
    if (bowser.platform.type === 'mobile' || bowser.platform.type === 'tablet') {
      hasMobileWarningDisplayed = sessionStorage.getItem('hasMobileWarningDisplayed');
    }
    const displayMobileWarningMsg = hasMobileWarningDisplayed ? '' : this.warningMessageCompose();

    const getErrorLogEndpoint = configureRequest('/error');

    return (
      <ErrorBoundary
        appName='ReturnsApp'
        errorLog={(body) => axios.post(getErrorLogEndpoint, body)}
      >
        {displayMobileWarningMsg}
        <div id='main'>
          <div className='wrap clear'>
            <section id='content' className='clear'>
              <article className='pushleft px880 print'>
                <div className='no-print'>
                  {loading && (
                    <div
                      className='no-print'
                      style={{
                        zIndex: 99999,
                        position: 'absolute',
                        background: 'rgba(255,255,255,0.4)',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        paddingTop: '400px',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }}
                    >
                      <img
                        src={loadingGif}
                        alt='loading...'
                      />
                    </div>
                  )}

                  <div className='header-title'>
                    <h1 className='section-title'>
                      {title}
                    </h1>
                  </div>

                  {apiError && (
                    <h3 style={{ color: 'red' }}>{apiError}</h3>
                  )}

                  {hasReturns && (
                    <p style={{ marginBottom: '3px' }}>
                      Below are the performance returns for {formatDropDownDate(selectedMonth.Date)}. Remember, past
                      performance is not indicative of future performance. To view the previous month, select the date from
                      the drop down menu below. To view a footnote, simply hover your cursor over the footnote number.
                    </p>
                  )}

                  <p>
                    The value of your my529 account may vary depending on market conditions and the performance of the
                    investment option you select. It could be more or less than the amount you contribute. Your investment
                    could lose value. Past performance is no guarantee of future returns. You assume all investment risks,
                    including potential for loss of principal as well as responsibility for any federal and/or state
                    consequences. Accounts are opened based on representations by you and with your express agreement to
                    the Terms and Conditions of the my529 Account Agreement. Information on this website does not
                    supersede the information in the Program Description and the actual written contracts and agreements
                    that establish my529 accounts.
                  </p>

                  {hasReturns && (
                    <p className='select'>
                      <strong>View investment option performance as of:</strong>
                      <select
                        onChange={(e) => this.updateIndex(e.target.value)}
                        value={index}
                      >
                        <option value='0'>{formatDropDownDate(months[0].Date)}</option>
                        <option value='1'>{formatDropDownDate(months[1].Date)}</option>
                        <option value='2'>{formatDropDownDate(months[2].Date)}</option>
                      </select>
                    </p>
                  )}

                  {hasReturns && (
                    <p className='in-page-links'>
                      View the returns for:&nbsp;
                      {selectedMonth.targetEnrollment.length > 0 && <span><a href='#targetEnrollment'>Target-Enrollment Date Investment Options</a> | </span>}
                      {selectedMonth.static.length > 0 && <span><a href='#static'>Static Investment Options</a> | </span>}
                      {selectedMonth.customized.length > 0 && <a href='#custom'>Customized Funds</a>}
                    </p>
                  )}

                  {hasYields && (
                    <p className='in-page-links'>
                      View the current rates/yields:&nbsp;
                      <span><a href='#currentRatesYields'>FDIC Insured and Stable Value</a></span>
                    </p>
                  )}
                </div>

                {hasReturns && (
                  <h3 className='text-right table-preface print-title no-screen'>
                    Investment Option Performance as of {formatDropDownDate(selectedMonth.Date)}
                  </h3>
                )}

                {hasReturns && (
                  <h3 className='text-right table-preface no-print'>
                    my529 Investment Option Performance as of {formatDropDownDate(selectedMonth.Date)}
                  </h3>
                )}

                {selectedMonth.amendedDate && (
                  <h3 className='table-preface'>
                    These returns were amended and restated on {formatDropDownDate(selectedMonth.amendedDate)}
                  </h3>
                )}

                {!hasReturns && (
                  <div style={{ marginTop: '15px' }}>
                    <h3>The my529 Investment Performance Page is Temporarily Down for Maintenance</h3>
                    <p>We are performing scheduled maintenance. We should be back online shortly. Thank you for your
                      patience.</p>
                  </div>
                )}

                <a id='targetEnrollment' className='no-print' />
                {selectedMonth.targetEnrollment.length > 0 &&
                  <TargetEnrollmentTable selectedMonth={selectedMonth} titleLink='https://my529.org/investment-options/#target-enrollment-content' />}
                <div className='no-screen'>
                  <Legal />
                </div>

                <a id='static' className='no-print' />
                {selectedMonth.static.length > 0 &&
                  <StaticTable selectedMonth={selectedMonth} titleLink='https://my529.org/investment-options/#static-content' />}

                {selectedMonth.customized.length > 0 &&
                  <p className='no-print'>
                    <a href='https://my529.org/investment-options/underlying-investments/' target='_blank' rel='noopener noreferrer'>Click here</a> to view a list of customized investment options and their underlying funds. The third character in the ticker symbol indicates if the underlying fund is a Vanguard (V) or Dimensional (D) fund.
                  </p>}

                <a id='custom' className='no-print' />
                {selectedMonth.customized.length > 0 &&
                  <CustomizedTable selectedMonth={selectedMonth} titleLink='https://my529.org/investment-options/' />}

                <a id='currentRatesYields' className='no-print' />
                {hasYields && (
                  <FundUnderlyingRateInformation FDICRate={FDICRate} FDICYield={FDICYield} StableValueYield={StableValueYield} />
                )}

                {hasReturns && <InfoSection />}

                {hasReturns && <Footnotes />}

                <div className='no-print'>
                  <Legal />
                </div>

                <div
                  className='print-wrapper no-print text-center'
                  id='printButton'
                  style={{ fontSize: '24px' }}
                  onClick={ReturnsApp.print}

                >
                  <span style={{ position: 'relative' }} data-tooltip-top='Print'>
                    <span className='glyphicon glyphicon-print' />
                  </span>
                </div>

                <div className={`scroll-top-wrapper no-print ${showScroll && 'show'}`}
                  onClick={ReturnsApp.handleScrollButtonClick}
                >
                  <div
                    data-tooltip-top='Scroll to Top'
                    style={{
                      position: 'relative'
                    }}
                  >
                    <span
                      className='glyphicon glyphicon-upload'
                      style={{
                        fontSize: '42px'
                      }}
                    />
                  </div>
                </div>
              </article>
            </section>
          </div>
        </div>
      </ErrorBoundary>
    );
  }
}

export default ReturnsApp;
